import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from 'src/common/button';
import { useNewJourneyStore } from 'src/dashboard/store/new-journey';
import { useCurrentOrganization } from 'src/store/organization';
import { NewJourneyType } from 'src/common/interfaces/journey/new-journey-type.enum';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { apiAddOrganizationAction, apiCreateOrUpdateNode, apiAddNodeToJourney, apiGetEmbedDetailsFromUrl, apiCreateJourney, apiCreateContent, apiCreateJourneyWithContent } from 'src/utils/journeyApi';
import { apiSegmentTrackAnonymous } from 'src/utils/segment';
import { Nullable } from 'src/types/nullable.type';
import classNames from 'classnames';
import isNull from 'lodash/isNull';
import { useCurrentUser } from 'src/store/user';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { WizardContentSectionAddLinks } from 'src/common/components/wizard/content/section/add-links';
import { FileWithMeta, WizardContentSectionUploadFile } from 'src/common/components/wizard/content/section/upload-file';
import { uploadFileToS3, uploadImageAssetToS3, uploadVideoFileToS3, uploadFileAssetToS3 } from 'src/common/helpers/file-upload';
import { createPdfBlockLayout, createVideoBlockLayout } from 'src/editor/content-creation/layout-manager';

import { ContentTypeEnum } from 'src/common/interfaces/content/type.enum';
import { getFormParamsByNode } from 'src/dashboard/library/content/helpers/get-form-params-by-node';
import { useDashboardLibraryContentLinkStore } from 'src/dashboard/library/content/link-store';
import { useFilesUploadStore } from 'src/common/store/files-upload.store';
import { Node } from 'src/common/interfaces/node.interface';
import { Journey } from 'src/common/interfaces/journey.interface';
import { racePromiseWithTimeout } from 'src/common/helpers/race-promise-with-timeout';
import { Token, useApiIntegrations } from 'src/common/api/use-integrations.hook';
import { IntegrationConnectButton } from 'src/editor/content-creation/media/connect-button';
import { IntegrationGooglePickerButton } from 'src/editor/content-creation/media/google-picker-button';
import { NeueLoadingSpinner } from 'src/editor/content-creation/components/loading/spinner';
import { RadioGroup, RadioGroupItem } from 'src/common/form/radio-group';
import { v4 as uuidv4 } from 'uuid';
import VideoDemoModal from '../components/create-journey/VideoDemoModal';
import { generateKeyBetween } from 'fractional-indexing';
import { createImageBlockLayout, createFileBlockLayout } from 'src/editor/content-creation/layout-manager';
import { createLinkBlockLayout } from 'src/editor/content-creation/links/layout';
import { LinkResponse } from 'src/editor/content-creation/links/types';

// Custom implementation of content cards
type OnboardingContentStack = {
  type: 'existing_content' | 'file' | 'url';
  content: Node | File;
  meta?: string;
};

type OnboardingContentCardType = Nullable<OnboardingContentStack> | undefined;
type OnboardingContentCardsType = OnboardingContentCardType[];

const PLACEHOLDER_CARD_COUNT = 7;
const INITIAL_PLACEHOLDER_CARDS = Array(PLACEHOLDER_CARD_COUNT).fill(null);
const CREATING_NODE_MAX_DURATION = 3000;

export const PageOnboardingCreateJourney = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isCreatingJourney, setIsCreatingJourney] = useState(false);
  const { setErrorNotification } = useNotificationMessage();
  
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const { currentUser } = useCurrentUser();
  const { setLoading: setContentLinkStoreLoading, resetStore: resetContentLinkStore } = useDashboardLibraryContentLinkStore();
  const { lastCancelledFileName, removeCancelledFileName, hasOngoingUploadCall } = useFilesUploadStore(
    (state) => ({
      lastCancelledFileName: state.lastCancelledFileName,
      removeCancelledFileName: state.removeLastCancelledFileName,
      hasOngoingUploadCall: state.getOngoingUploadStacks().length !== 0,
    })
  );
  
  const [contentCards, setContentCards] = useState<OnboardingContentCardsType>([...INITIAL_PLACEHOLDER_CARDS]);
  const [nodeCreationStack, setNodeCreationStack] = useState<Array<() => Promise<Node>>>([]);
  const [createdJourney, setCreatedJourney] = useState<Nullable<Journey>>(null);
  const isErrorNotificationShownRef = useRef(false);
  const lastElementRef = useRef<Nullable<HTMLDivElement>>(null);
  
  const { setJourneyType, createJourney } = useNewJourneyStore((state) => ({
    setJourneyType: state.setJourneyType,
    createJourney: state.createJourney
  }));

  // Google Drive Integration State
  const [gdLoaded, setGdLoaded] = useState(false);
  const { fetchAccessTokens } = useApiIntegrations();
  const [gdTokens, setGdTokens] = useState<Token[]>([]);
  
  // Journey Type State
  const [selectedJourneyType, setSelectedJourneyType] = useState<NewJourneyType>(NewJourneyType.WIZARD); // Default to WIZARD or choose a suitable default

  // Get workspace information
  const { getMemberWorkspaces } = useCurrentOrganization();
  const memberWorkspaces = getMemberWorkspaces(currentUser);
  const [publicWorkspace] = memberWorkspaces.filter(({ workspace_type }) => workspace_type === 'public');

  const [videoDemoOpen, setVideoDemoOpen] = useState(false);
  const [videoDemoUrl, setVideoDemoUrl] = useState<string>('');
  const [videoDemoTitle, setVideoDemoTitle] = useState<string>('');

  // URLs for the demo videos
  const PITCH_MODE_VIDEO_URL = 'http://dashboardcdn.journey.io/onboarding-videos/microsite-mode.mp4';
  const MICROSITE_MODE_VIDEO_URL = 'http://dashboardcdn.journey.io/onboarding-videos/pitch-mode.mp4';

  useEffect(() => {
    if (lastCancelledFileName) {
      const indexToRemove = contentCards.findIndex((card) => card?.content.name === lastCancelledFileName);
      onContentDeleted(indexToRemove);
      removeCancelledFileName();
    }
  }, [lastCancelledFileName]);

  // Load Google Drive tokens
  const loadGdTokens = useCallback(() => {
    setGdLoaded(false); // Start loading
    fetchAccessTokens('google-drive')
      .then((tokens: Token[]) => {
        setGdTokens(tokens);
        setGdLoaded(true);
      })
      .catch((err) => {
        console.error('Error fetching Google Drive tokens:', err);
        setGdLoaded(true); // Finish loading even on error
      });
  }, [fetchAccessTokens]);

  useEffect(() => {
    loadGdTokens();
  }, [loadGdTokens]);

  // Helper to create multiple time-delayed progress updates
  const initialProgress = useCallback((count: number) => {
    const initialProgressTimeoutId = setTimeout(() => {
      setProgress(10);
      clearTimeout(initialProgressTimeoutId);
    }, 350);
    
    let internalCount = 0;
    const progressInterval = setInterval(() => {
      if (internalCount === count) {
        clearInterval(progressInterval);
      }
      setProgress(prevProgress => prevProgress + Math.floor(Math.random() * (14 - 2 + 1) + 2));
      internalCount += 1;
    }, 700);
  }, []);

  const skipJourneyCreation = useCallback(async () => {
    try {
      setLoading(true);
      await apiAddOrganizationAction(currentOrganization.id, 'onboarding_completed');
      window.location.replace('/');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setLoading(false);
    }
  }, [currentOrganization.id]);

  const startJourneyCreation = useCallback(async () => {
    try {
      setIsCreatingJourney(true);
      setLoading(true);
      
      // Set journey type based on selection
      setJourneyType(selectedJourneyType);
      
      let journey: Nullable<Journey> = null;
      
      // Default recipient values
      const recipient_domain = '';
      const recipient_logo_url = '';
      const recipient_name = '';

      if (selectedJourneyType === NewJourneyType.NEUE_SCRATCH) {
        // Use the new approach for Neue journeys
        const uuid = uuidv4().replace(/-/g, '').substring(0, 6);
        
        // Process file uploads first
        const filePromises = contentCards
          .filter(card => card && card.type === 'file')
          .map(async (card) => {
            if (!card) return null;
            const file = card.content as File;
            try {
              // Choose appropriate upload function based on file type
              let uploadResponse;
              
              if (file.type.startsWith('image/')) {
                uploadResponse = await uploadImageAssetToS3(file);
              } else if (file.type.startsWith('video/')) {
                uploadResponse = await uploadVideoFileToS3(file);
              } else {
                uploadResponse = await uploadFileAssetToS3(file);
              }

              return {
                name: file.name,
                url: uploadResponse.url,
                type: file.type,
                size: file.size
              };
            } catch (error) {
              console.error('Failed to upload file:', file.name, error);
              return null;
            }
          });
        
        // Collect URLs from content cards
        const urls = contentCards
          .filter((card): card is OnboardingContentStack & { type: 'url'; content: Node } => card != null && card.type === 'url')
          .map(({ content: node }) => node.external_link?.url || node.embed?.url || '')
          .filter((url) => !!url) as string[];
        
        // Wait for all file uploads to complete
        const uploadedFiles = (await Promise.all(filePromises)).filter(Boolean) as any[];

        // Initialize sections array
        const precreatedSections: Array<{ id: string; title: string; blocks: any[] }> = [];

        // Handle files: one section per file
        for (const file of uploadedFiles) {
          const sectionId = uuidv4().replace(/-/g, '');
          const blockId = uuidv4().replace(/-/g, '');
          const isImage = file.type.startsWith('image/');
          const isVideo = file.type.startsWith('video/');
          const isPdf = file.type === 'application/pdf';
          // choose proper layout for each file type
          let layoutSpec;
          if (isImage) {
            layoutSpec = createImageBlockLayout('100%', { aspectRatio: null });
          } else if (isVideo) {
            layoutSpec = createVideoBlockLayout('100%', { aspectRatio: null });
          } else if (isPdf) {
            layoutSpec = createPdfBlockLayout('100%', { aspectRatio: null });
          } else {
            layoutSpec = createFileBlockLayout('100%');
          }

          // Determine the correct block content type
          let blockContentType: 'image' | 'video' | 'pdf' | 'attachment' = 'attachment';
          if (isImage) {
            blockContentType = 'image';
          } else if (isVideo) {
            blockContentType = 'video';
          } else if (isPdf) {
            blockContentType = 'pdf';
          }

          const fileBlock = {
            id: blockId,
            position: generateKeyBetween(null, null),
            content: {
              type: blockContentType,
              url: file.url,
              name: file.name,
              metadata: { name: file.name, size: file.size, type: file.type },
              objectFit: 'contain',
            },
            layout: layoutSpec,
          };
          precreatedSections.push({ id: sectionId, title: file.name, blocks: [fileBlock] });
        }

        // Handle URLs: one section per URL with proper LinkResponse
        for (const urlValue of urls) {
          const sectionId = uuidv4().replace(/-/g, '');
          const blockId = uuidv4().replace(/-/g, '');
          const linkResp: LinkResponse = {
            url: urlValue,
            transformed_url: urlValue,
            title: urlValue,
            thumbnail: { url: null, width: null, height: null },
            destination: null,
            iframe_safe: true,
            third_party_iframe_safe: true,
          };
          const linkBlock = {
            id: blockId,
            position: generateKeyBetween(null, null),
            content: { type: 'link', link: linkResp, displayOption: 'embed' },
            layout: createLinkBlockLayout('100%'),
          };
          precreatedSections.push({ id: sectionId, title: linkResp.title, blocks: [linkBlock] });
        }

        console.log('precreatedSections', precreatedSections);
         
        // Create journey with uploaded content
        journey = await apiCreateJourneyWithContent(
          currentOrganization.id,
          publicWorkspace.id,
          {
            name: `My Journey`,
            slug: `my-journey-${uuid}`,
            data: { neue: true },
            files: uploadedFiles,
            urls,
            precreatedSections,
            recipient_domain,
            recipient_logo_url,
            recipient_name,
          }
        );
      } else {
        // Create a regular wizard journey
        journey = await createJourney();
        
        // Process content cards and create nodes
        const nodePromises = contentCards.map(async (card) => {
          if (!card) return null;
          
          if (card.type === 'url') {
            const node = card.content as Node;
            return node;
          } else if (card.type === 'file') {
            const file = card.content as File;
            try {
              // Choose appropriate upload function based on file type
              let uploadResponse;
              let nodeType;
              
              if (file.type.startsWith('image/')) {
                uploadResponse = await uploadImageAssetToS3(file);
                nodeType = 'image';
              } else if (file.type.startsWith('video/')) {
                uploadResponse = await uploadVideoFileToS3(file);
                nodeType = 'video';
              } else {
                uploadResponse = await uploadFileAssetToS3(file);
                nodeType = 'file';
              }

              // First create content for the file
              const content = await apiCreateContent(currentOrganization.id, {
                file_url: uploadResponse.url,
                file_content_type: uploadResponse.type,
                name: uploadResponse.name,
              });

              // Then create node with the content
              return await apiCreateOrUpdateNode(currentOrganization.id, publicWorkspace.id, nodeType, null, {
                name: file.name,
                content_id: content.id,
                file_url: uploadResponse.url,
                file_type: file.type,
                file_size: file.size
              });
            } catch (error) {
              console.error('Failed to process file:', file.name, error);
              return null;
            }
          }
          return null;
        });

        // Wait for all nodes to be created
        const nodes = (await Promise.all(nodePromises)).filter(Boolean);
        
        // Add nodes to journey's first step sequentially to preserve order
        const [firstStep] = journey.steps || [];
        if (firstStep && nodes.length > 0) {
          let previousNodeUuid: any = null;
          for (const node of nodes) {
            if (previousNodeUuid) {
              await apiAddNodeToJourney(node.uuid, firstStep.id, previousNodeUuid as any);
            } else {
              await apiAddNodeToJourney(node.uuid, firstStep.id);
            }
            previousNodeUuid = node.uuid;
          }
        }
      }
      
      setCreatedJourney(journey);
      
      // Set initial progress
      initialProgress(3);
      
      // Complete the journey creation
      setProgress(100);
      
      // Mark onboarding as completed
      await apiAddOrganizationAction(currentOrganization.id, 'onboarding_completed');
      
      // Track event
      apiSegmentTrackAnonymous('Journey Created During Onboarding', {
        journey_type: selectedJourneyType === NewJourneyType.NEUE_SCRATCH ? 'neue' : 'wizard',
        organization_id: currentOrganization.id
      });
    } catch (error) {
      console.error('Error creating journey:', error);
      setLoading(false);
      setIsCreatingJourney(false);
      setErrorNotification('Something went wrong creating your journey.');
    }
  }, [selectedJourneyType, currentOrganization.id, publicWorkspace.id, contentCards]);

  const createJourneyWithSteps = async (journey: Journey) => {
    if (journey) {
      const remainingProgressForNodes = 100 - progress;
      const [stepToAddNode] = journey.steps;
      
      for (const creator of nodeCreationStack) {
        try {
          const node = await creator();
          await apiAddNodeToJourney(node.uuid, stepToAddNode.id, null);
          setProgress(prevProgress => Math.min(Math.ceil(prevProgress + remainingProgressForNodes / nodeCreationStack.length), 80));
        } catch (error) {
          console.error('Error adding node to journey:', error);
        }
      }
      
      // Complete the journey creation
      setProgress(100);
      
      // Mark onboarding as completed
      await apiAddOrganizationAction(currentOrganization.id, 'onboarding_completed');
      
      // Track event
      apiSegmentTrackAnonymous('Journey Created During Onboarding', {
        journey_type: 'wizard',
        organization_id: currentOrganization.id
      });
    }
  };

  // Content handling functions
  const updateListPlacement = (stack: OnboardingContentStack) => {
    const indexToPlace = contentCards.findIndex((card) => isNull(card));
    const newCards = [...contentCards];
    
    if (indexToPlace === -1) {
      newCards.push(stack);
    } else {
      newCards[indexToPlace] = stack;
    }
    
    setContentCards(newCards);
  };

  const scrollListBottom = () => {
    if (lastElementRef.current) {
      lastElementRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const createUrlContent = async (name: string, url: string) => {
    const isURLTargetToMail = url.includes('@');
    let contentType = ContentTypeEnum.EXTERNAL_LINK;
    let contentTypeParam = 'link_url';
    let contentName = name;
    const urlToAdd = isURLTargetToMail && !url.includes('mailto:') ? `mailto:${url}` : url;
    
    if (!isURLTargetToMail) {
      try {
        const response = await apiGetEmbedDetailsFromUrl(urlToAdd);
        if (response.meta_data) {
          contentName = response.meta_data.title;
        }
        if ((response.transformed_url && response.iframe_safe) || response.third_party_iframe_safe) {
          contentType = ContentTypeEnum.EMBED;
          contentTypeParam = 'url';
        }
      } catch (err) {
        console.error(err);
      }
    }
    
    return racePromiseWithTimeout(
      apiCreateOrUpdateNode(currentOrganization.id, publicWorkspace.id, contentType, null, {
        name: contentName,
        [contentTypeParam]: urlToAdd,
      }).catch((err) => {
        if (!isErrorNotificationShownRef.current) {
          isErrorNotificationShownRef.current = true;
          setErrorNotification('Something went wrong.');
        } else {
          isErrorNotificationShownRef.current = false;
        }
      }),
      CREATING_NODE_MAX_DURATION
    ).catch((err) => {
      if (!isErrorNotificationShownRef.current) {
        isErrorNotificationShownRef.current = true;
        setErrorNotification('Something went wrong.');
      } else {
        isErrorNotificationShownRef.current = false;
      }
    });
  };

  // Handle Google Drive file selection
  const onGoogleDriveFileSelected = async (embedUrl: string, title: string) => {
    setContentLinkStoreLoading(true); // Use the same loading indicator
    try {
      const node = await createUrlContent(title, embedUrl);
      if (node) {
        const addedStack: OnboardingContentStack = { type: 'url', content: node, meta: 'Google Drive' }; // Mark as URL type, add meta info
        updateListPlacement(addedStack);
        stackNodeCreation(node.content_type, getFormParamsByNode(node));
      }
    } catch (error) {
      console.error("Error processing Google Drive file:", error);
      // setErrorNotification('Failed to add Google Drive file.'); // Optionally add specific error
    } finally {
      resetContentLinkStore(); // Reset loading state
    }
  };

  const onLinksAdded = (links: string[]) => {
    setContentLinkStoreLoading(true);
    Promise.all(links.map((link) => createUrlContent(link, link))).then((nodes: Node[]) => {
      const addedStacks: OnboardingContentStack[] = nodes
        .filter((node) => typeof node !== 'undefined')
        .map((node) => ({ type: 'url', content: node }));
      
      addedStacks.forEach(updateListPlacement);
      
      nodes
        .filter((node) => typeof node !== 'undefined')
        .forEach((node) => {
          stackNodeCreation(node.content_type, getFormParamsByNode(node));
        });
      
      resetContentLinkStore();
    });
  };

  const onLibraryContentPicked = async (node: Node) => {
    const addedStack: OnboardingContentStack = { type: 'existing_content', content: node };
    updateListPlacement(addedStack);
    stackNodeCreation(node.content_type, getFormParamsByNode(node));
  };

  const onFilesSelected = (files: FileWithMeta[]) => {
    // Add multiple file stacks at once using functional state update to avoid stale state
    const addedStacks: OnboardingContentStack[] = files.map(({ file, meta }) => ({ type: 'file', content: file, meta }));
    setContentCards((prevCards) => {
      const newCards = [...prevCards];
      addedStacks.forEach((stack) => {
        const idx = newCards.findIndex((card) => card === null || card === undefined);
        if (idx === -1) {
          newCards.push(stack);
        } else {
          newCards[idx] = stack;
        }
      });
      return newCards;
    });
  };

  const onContentDeleted = (indexToRemove: number) => {
    if (indexToRemove >= 0) {
      const newCards = [...contentCards];
      newCards[indexToRemove] = undefined;
      setContentCards(newCards.filter((card) => typeof card !== 'undefined'));
      
      // Remove the node creation function at the same index
      const newNodeCreationStack = [...nodeCreationStack];
      newNodeCreationStack.splice(indexToRemove, 1);
      setNodeCreationStack(newNodeCreationStack);
    }
  };

  const stackNodeCreation = (contentType: string, payload?: object) => {
    const newNodeCreationStack = [...nodeCreationStack];
    newNodeCreationStack.push(() => apiCreateOrUpdateNode(currentOrganization.id, publicWorkspace.id, contentType, null, payload));
    setNodeCreationStack(newNodeCreationStack);
  };

  const hideBuildCard = contentCards.every(isNull);

  // Render a simplified version of loading screen when journey is being created
  if (isCreatingJourney) {
    return (
      <>
        <Helmet>
          <title>Journey | Creating Your Journey</title>
        </Helmet>
        <div className="flex flex-col items-center justify-center min-h-screen bg-white p-8">
          <h1 className="text-3xl font-serif text-gray-900 mb-6">
            Compiling your story…
          </h1>
          <video
            className="w-full max-w-2xl h-96 rounded-lg object-cover"
            src={"https://dashboardcdn.journey.io/onboarding-videos/intro-to-journey.mp4"}
            autoPlay
            controls
            muted={false}
            playsInline
          >
            Your browser does not support the video tag.
          </video>
          {createdJourney && (
            <Button
              variant="primary"
              size="regular"
              className="mt-6"
              label="Proceed to my journey"
              onClick={() => {
                // Set flag in sessionStorage to trigger editor onboarding tour
                try{
                  localStorage.setItem('fromCreateJourney', 'true');
                }
                catch(err){
                  console.log("error in onboarding")
                }
               
                
                if (selectedJourneyType === NewJourneyType.NEUE_SCRATCH) {
                  window.location.replace(`/journeys/neue-edit/${createdJourney.uuid}`);
                } else {
                  window.location.replace(`/journeys/edit/${createdJourney.uuid}`);
                }
              }}
            />
          )}
        </div>
      </>
    );
  }

  // Similar card rendering as in the wizard
  const renderContentCard = (card: OnboardingContentCardType, index: number) => {
    if (!card) return null;
    
    const { type, content } = card;
    
    let cardContent;
    if (type === 'url') {
      const node = content as Node;
      const url = node.external_link?.url || '';
      cardContent = (
        <div className="flex flex-col">
          <span className="text-sm font-medium truncate">{node.name}</span>
          <span className="text-xs text-gray-500 truncate">{url}</span>
        </div>
      );
    } else if (type === 'file') {
      const file = content as File;
      cardContent = (
        <div className="flex flex-col">
          <span className="text-sm font-medium truncate">{file.name}</span>
          <span className="text-xs text-gray-500">{Math.round(file.size / 1024)} KB</span>
        </div>
      );
    } else if (type === 'existing_content') {
      const node = content as Node;
      cardContent = (
        <div className="flex flex-col">
          <span className="text-sm font-medium truncate">{node.name}</span>
          <span className="text-xs text-gray-500 truncate">From library</span>
        </div>
      );
    }
    
    return (
      <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
        {cardContent}
        <button 
          className="text-gray-400 hover:text-gray-600"
          onClick={() => onContentDeleted(index)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Journey | Create a Journey</title>
      </Helmet>
      <VideoDemoModal open={videoDemoOpen} onClose={() => setVideoDemoOpen(false)} videoUrl={videoDemoUrl} videoTitle={videoDemoTitle} />
      {loading ? (
        <LoadingSpinner containerClassName="pt-xl" />
      ) : (
        <div className="min-h-screen bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row gap-8 justify-center items-start max-w-[920px] mx-auto">
              {/* Left Column - Content Addition */}
              <div className="w-full lg:w-[336px] flex-shrink-0">
                <div className="mb-6">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Create a Journey</h1>
                  <p className="text-gray-600">
                    Create your first journey by adding content from various sources.
                  </p>
                </div>
                
                <div className="flex flex-col mt-4">
                  <WizardContentSectionAddLinks onAddLinks={onLinksAdded} onLinkNotFound={resetContentLinkStore} />
                  <WizardContentSectionUploadFile onSelectFiles={onFilesSelected} />
                  
                  {/* Google Drive Integration Section */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h3 className="text-bedrock-p-strong mb-2">Google Drive</h3>
                    {!gdLoaded && <NeueLoadingSpinner theme='journey' size='16' className='h-8' />}
                    {gdLoaded && gdTokens.length === 0 && (
                      <div className='flex flex-col space-y-2'>
                        <p className='text-xs text-gray-500'>Connect Google Drive to import files.</p>
                        <IntegrationConnectButton onIntegrationConnected={loadGdTokens} provider={'google-drive'} />
                      </div>
                    )}
                    {gdLoaded && gdTokens.map((token: Token) => (
                      <div key={token.email} className='flex flex-col space-y-2'>
                        <div className='text-xs text-gray-600'>Connected as: {token.email}</div>
                        <IntegrationGooglePickerButton token={token} onFileSelected={onGoogleDriveFileSelected} />
                      </div>
                    ))}
                  </div>
                  
                  {/* Journey Type Selection Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-bedrock-p-strong mb-3">Journey Type</h3>
                    <RadioGroup<NewJourneyType>
                      name="journeyType"
                      options={[
                        {
                          value: NewJourneyType.WIZARD,
                          label: (
                            <div>
                              <span className="font-medium text-gray-900">Pitch Mode</span>
                              <p className="text-sm text-gray-500 mt-1">Powerpoint presentation style ideal for guided walkthroughs.</p>
                              <button
                                type="button"
                                onClick={e => { e.stopPropagation(); setVideoDemoUrl(PITCH_MODE_VIDEO_URL); setVideoDemoTitle('Pitch mode demo'); setVideoDemoOpen(true); }}
                                className="text-sm text-blue-600 hover:underline mt-1 inline-block focus:outline-none"
                              >
                                View Demo
                              </button>
                            </div>
                          ),
                        },
                        {
                          value: NewJourneyType.NEUE_SCRATCH,
                          label: (
                            <div>
                              <span className="font-medium text-gray-900">Microsite Mode</span>
                              <p className="text-sm text-gray-500 mt-1">Create engaging single-page experiences for marketing or information.</p>
                              <button
                                type="button"
                                onClick={e => { e.stopPropagation(); setVideoDemoUrl(MICROSITE_MODE_VIDEO_URL); setVideoDemoTitle('Microsite mode demo'); setVideoDemoOpen(true); }}
                                className="text-sm text-blue-600 hover:underline mt-1 inline-block focus:outline-none"
                              >
                                View Demo
                              </button>
                            </div>
                          ),
                        },
                      ]}
                      value={String(selectedJourneyType)}
                      onChange={(item) => setSelectedJourneyType(item.value)}
                      alignment="vertical"
                      className="space-y-3"
                      itemClassName="flex items-start space-x-3 p-3 border rounded-md cursor-pointer hover:bg-gray-50"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column - Content List */}
              <div className="w-full lg:w-[520px] flex-shrink-0">
                <div className="bg-bedrock-light-gray rounded-lg p-6 h-full min-h-[600px] relative">
                  <div className="flex flex-col h-full">
                    <span className="text-base font-medium mb-4">Your Added media</span>
                    
                    {/* Scrollable content area */}
                    <div className="flex-1 overflow-y-auto mb-20">
                      <div className="space-y-2">
                        {contentCards.filter(Boolean).map((card, index) => renderContentCard(card, index))}
                      </div>
                      <div ref={lastElementRef} />
                    </div>

                    {/* Fixed bottom buttons */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-bedrock-light-gray">
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button
                          variant="secondary"
                          size="regular"
                          className="w-full sm:w-1/2"
                          label="Do it Later"
                          onClick={skipJourneyCreation}
                          disabled={hasOngoingUploadCall}
                        />
                        <Button
                          variant="primary"
                          size="regular"
                          className="w-full sm:w-1/2"
                          label="Build Journey"
                          onClick={startJourneyCreation}
                          disabled={hasOngoingUploadCall || (contentCards && contentCards.filter(Boolean).length === 0)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PageOnboardingCreateJourney; 